import * as R from 'ramda';

import { PANEL_OPTION_KEYS, GRAPH_FILTER_TYPES } from '@consts';

const {
    SORTING_METRIC_EXPOSURE,
    TABLE_METRIC,
    BREAKDOWN: BREAKDOWN_RAW_TYPE,
} = PANEL_OPTION_KEYS;
const {
    YAXIS,
    XAXIS,
    BUBBLE_SIZE,
    COMBO_YAXIS_1,
    COMBO_YAXIS_2,
    COMBO_YAXIS_3,
    BREAKDOWN,
    DATE_BREAKDOWN,
} = GRAPH_FILTER_TYPES;

/**
 * widget.exposureList에 저장되는 key 이름
 *
 * React 측 EXPOSURE_LIST_KEY와 동일해야 함
 */
const EXPOSURE_LIST_KEY = {
    // metric
    PRIMARY_METRIC: 'primaryMetric',
    SECONDARY_METRIC: 'secondaryMetric',
    BUBBLE_SIZE_METRIC: 'bubbleSizeMetric',
    COMBO_METRIC_2: 'comboMetric2',
    COMBO_METRIC_3: 'comboMetric3',
    SORTING_METRIC: 'sortMetric',

    // breakdown
    BREAKDOWN: 'breakdown',
    DATE_BREAKDOWN: 'dateBreakdown',
};

/**
 * "all" = no grouping (breakdown)
 * "" = none (metric)
 */

const EMPTY_METRIC_OPTION = '';
const NO_GROUPING_BREAKDOWN_OPTION = 'all';

const DEFAULT_METRIC_LIST = [
    'clicks',
    'budget',
    'spend',
    'impressions',
    'cpc',
    'ctr',
    'cpm',
];

const DEFAULT_BREAKDOWN_LIST = ['channel', 'campaign', 'adSet', 'ad'];
const DEFAULT_DATE_BREAKDOWN_LIST = [
    'autoTime',
    'day',
    'week',
    'month',
    ...DEFAULT_BREAKDOWN_LIST,
];

/**
 * Exposure Key 별 설정 (widget setting panel option key 기준으로 키잉)
 * - fieldType:             'metric' | 'breakdown' (availableFields API fieldType과 동일)
 * - rawType:               enrichment 시 사용할 raw data type (TABLE_METRIC | BREAKDOWN)
 * - exposureSettingType:   'metric' | 'comboMetric' | 'breakdown' (렌더링/분기용)
 *
 * - fieldListKey:          exposureListMap에서 사용할 key (리스트 공유 시 다른 key를 참조)
 * - defaultFieldList:      exposureList가 nil일 때 칩/드롭다운에 표시할 기본 필드 목록
 *
 * - selectedPath:          widget에서 선택값이 저장되는 경로
 */
export const EXPOSURE_KEY_CONFIG = {
    /**
     * Creative
     */
    [SORTING_METRIC_EXPOSURE]: {
        rawType: TABLE_METRIC,
        fieldListKey: EXPOSURE_LIST_KEY.SORTING_METRIC,
        selectedPath: ['sort', 'name'],
        fieldType: 'metric',
        exposureSettingType: 'metric',
        defaultFieldList: DEFAULT_METRIC_LIST,
    },
    /**
     * Graph
     */
    [YAXIS]: {
        rawType: TABLE_METRIC,
        fieldListKey: EXPOSURE_LIST_KEY.PRIMARY_METRIC,
        selectedPath: ['selectedMetric'],
        fieldType: 'metric',
        exposureSettingType: 'metric',
        defaultFieldList: DEFAULT_METRIC_LIST,
    },
    [XAXIS]: {
        rawType: TABLE_METRIC,
        fieldListKey: EXPOSURE_LIST_KEY.SECONDARY_METRIC,
        selectedPath: ['selectedxAxis'],
        fieldType: 'metric',
        exposureSettingType: 'metric',
        defaultFieldList: DEFAULT_METRIC_LIST,
    },
    [BUBBLE_SIZE]: {
        rawType: TABLE_METRIC,
        fieldListKey: EXPOSURE_LIST_KEY.BUBBLE_SIZE_METRIC,
        selectedPath: ['selectedBubbleSize'],
        fieldType: 'metric',
        exposureSettingType: 'metric',
        defaultFieldList: DEFAULT_METRIC_LIST,
        fixedOptions: [EMPTY_METRIC_OPTION],
        selectedDefault: '',
    },
    [COMBO_YAXIS_1]: {
        rawType: TABLE_METRIC,
        fieldListKey: EXPOSURE_LIST_KEY.PRIMARY_METRIC, // YAXIS와 리스트 공유
        selectedPath: ['comboChart', 'yAxis1'],
        fieldType: 'metric',
        exposureSettingType: 'comboMetric',
        defaultFieldList: DEFAULT_METRIC_LIST,
    },
    [COMBO_YAXIS_2]: {
        rawType: TABLE_METRIC,
        fieldListKey: EXPOSURE_LIST_KEY.COMBO_METRIC_2,
        selectedPath: ['comboChart', 'yAxis2'],
        fieldType: 'metric',
        exposureSettingType: 'comboMetric',
        defaultFieldList: DEFAULT_METRIC_LIST,
        fixedOptions: [EMPTY_METRIC_OPTION],
        selectedDefault: '',
    },
    [COMBO_YAXIS_3]: {
        rawType: TABLE_METRIC,
        fieldListKey: EXPOSURE_LIST_KEY.COMBO_METRIC_3,
        selectedPath: ['comboChart', 'yAxis3'],
        fieldType: 'metric',
        exposureSettingType: 'comboMetric',
        defaultFieldList: DEFAULT_METRIC_LIST,
        fixedOptions: [EMPTY_METRIC_OPTION],
        selectedDefault: '',
    },
    [DATE_BREAKDOWN]: {
        rawType: BREAKDOWN_RAW_TYPE,
        fieldListKey: EXPOSURE_LIST_KEY.DATE_BREAKDOWN,
        selectedPath: ['dateBreakdown'],
        fieldType: 'breakdown',
        exposureSettingType: 'breakdown',
        defaultFieldList: DEFAULT_DATE_BREAKDOWN_LIST,
        fixedOptions: [NO_GROUPING_BREAKDOWN_OPTION],
        selectedDefault: 'autoTime',
    },
    [BREAKDOWN]: {
        rawType: BREAKDOWN_RAW_TYPE,
        fieldListKey: EXPOSURE_LIST_KEY.BREAKDOWN,
        selectedPath: ['dimensionIds', 0],
        fieldType: 'breakdown',
        exposureSettingType: 'breakdown',
        defaultFieldList: DEFAULT_BREAKDOWN_LIST,
        fixedOptions: [NO_GROUPING_BREAKDOWN_OPTION],
    },
};

export const GRAPH_EXPOSURE_KEY_CONFIG = R.omit([SORTING_METRIC_EXPOSURE])(
    EXPOSURE_KEY_CONFIG
);

export const CREATIVE_EXPOSURE_KEY_CONFIG = R.pick([SORTING_METRIC_EXPOSURE])(
    EXPOSURE_KEY_CONFIG
);
